import './style.css';
import { SceneManager } from './scene/SceneManager';
import { Pergola } from './pergola/Pergola';
import { Dimensions } from './pergola/dimensions';
import { ASSETS } from './scene/assets';
import { ConfigStore } from './config/store';
import { resolveConfig } from './config/resolve';
import { computeQuote } from './config/quote';
import { configSummaryRows } from './config/summary';
import { makeQuoteId } from './config/branding';
import { findOption } from './config/schema';
import { Wizard } from './ui/wizard';
import { SceneControls } from './ui/sceneControls';
import { stageOffsetZ } from './scene/world';
import { captureViews } from './export/views';
import { composeSheet, downloadSheetPNG, downloadSheetPDF } from './export/sheet';
import { buildQuotePayload, saveQuote } from './export/save';

/**
 * Punto de entrada de la aplicación.
 * Fase 3: store observable + wizard data-driven (attributes.json) conectados al
 * 3D en vivo. Cada atributo reconstruye/actualiza la pérgola; modos Estándar y
 * Personalizado funcionan.
 */
function init(): void {
  const canvas = document.getElementById('scene-canvas') as HTMLCanvasElement | null;
  const container = document.getElementById('viewport-pane') as HTMLElement | null;
  const configPane = document.getElementById('config-pane') as HTMLElement | null;
  if (!canvas || !container || !configPane) {
    console.error('[Teyco] No se encontraron los nodos del DOM necesarios.');
    return;
  }

  // Estado y overrides opcionales por URL (deep-link para pruebas/demo).
  const store = new ConfigStore();
  const initialLamasOpen = applyUrlOverrides(store);

  // Escena + pérgola con la configuración resuelta inicial.
  const sceneManager = new SceneManager(canvas, container);
  const initialConfig = resolveConfig(store.getState(), initialLamasOpen);
  const pergola = new Pergola(initialConfig);
  sceneManager.scene.add(pergola.group);
  sceneManager.addUpdatable((dt) => pergola.update(dt));
  sceneManager.frameOn(pergola.totalHeight);

  // Cotas dinámicas (ancho/largo/altura en mm).
  const dimensions = new Dimensions();
  dimensions.update(initialConfig);
  sceneManager.scene.add(dimensions.group);

  // Coloca pérgola + cotas: adosada se pega a la casa; independiente al jardín.
  const applyStage = (cfg: { tipo: typeof initialConfig.tipo; lengthMm: number }) => {
    const z = stageOffsetZ(cfg.tipo, cfg.lengthMm);
    pergola.group.position.z = z;
    dimensions.group.position.z = z;
  };
  applyStage(initialConfig);
  dimensions
    .loadFont(ASSETS.font)
    .catch((err) => console.error('[Teyco] Error cargando la fuente de cotas:', err));

  sceneManager.start();
  sceneManager
    .loadEnvironment()
    .then(() => console.info('[Teyco] HDRI cargado (IBL + cielo).'))
    .catch((err) => console.error('[Teyco] Error cargando el HDRI:', err));

  // Depuración: ?sheet=1 compone la ficha; ?save=1 ejecuta el guardado; ?still detiene
  // el bucle tras un instante (para capturas headless fiables del 3D en vivo).
  const params = new URLSearchParams(window.location.search);
  const debugSheet = params.has('sheet');
  const debugSave = params.has('save');
  if (params.has('still')) {
    window.setTimeout(() => {
      sceneManager.stop();
      const r = sceneManager.renderer;
      const c = r.domElement;
      const cam = sceneManager.camera;
      const tg = sceneManager.controls.target;
      console.info(
        `[Teyco][diag] canvas=${c.width}x${c.height} calls=${r.info.render.calls} tris=${r.info.render.triangles} cam=(${cam.position.x.toFixed(1)},${cam.position.y.toFixed(1)},${cam.position.z.toFixed(1)}) target=(${tg.x.toFixed(1)},${tg.y.toFixed(1)},${tg.z.toFixed(1)})`,
      );
    }, Number(params.get('still')) || 2500);
  }

  // Generación de la ficha-resumen (4 vistas acotadas + config + precio + branding).
  const generateSummary = async (format: 'png' | 'pdf'): Promise<void> => {
    const cfg = resolveConfig(store.getState(), pergola.lamasOpen);
    const quote = computeQuote(cfg);
    const views = captureViews(sceneManager, pergola, dimensions);
    const quoteId = makeQuoteId();
    const canvas = await composeSheet({
      views,
      configRows: configSummaryRows(store.getState()),
      quote,
      quoteId,
      date: new Date(),
    });
    if (format === 'pdf') downloadSheetPDF(canvas, `Teyco_${quoteId}.pdf`);
    else downloadSheetPNG(canvas, `Teyco_${quoteId}.png`);
  };

  // Guardar / solicitar presupuesto (JSON + POST + fallback localStorage, con imagen adjunta).
  const saveCurrentQuote = async () => {
    const cfg = resolveConfig(store.getState(), pergola.lamasOpen);
    const quote = computeQuote(cfg);
    const quoteId = makeQuoteId();
    const canvas = await composeSheet({
      views: captureViews(sceneManager, pergola, dimensions),
      configRows: configSummaryRows(store.getState()),
      quote,
      quoteId,
      date: new Date(),
    });
    const image = canvas.toDataURL('image/jpeg', 0.85);
    const payload = buildQuotePayload({ state: store.getState(), config: cfg, quote, quoteId, image });
    return saveQuote(payload);
  };

  // Wizard de configuración en el panel derecho.
  new Wizard(configPane, store, {
    onGenerateSummary: generateSummary,
    onSaveQuote: saveCurrentQuote,
  });

  if (debugSheet) {
    dimensions
      .loadFont(ASSETS.font)
      .then(async () => {
        const cfg = resolveConfig(store.getState(), pergola.lamasOpen);
        const canvas = await composeSheet({
          views: captureViews(sceneManager, pergola, dimensions),
          configRows: configSummaryRows(store.getState()),
          quote: computeQuote(cfg),
          quoteId: makeQuoteId(),
          date: new Date(),
        });
        Object.assign(canvas.style, {
          position: 'fixed',
          inset: '0',
          width: '100vw',
          height: '100vh',
          objectFit: 'contain',
          background: '#222',
          zIndex: '9999',
        });
        document.body.appendChild(canvas);
        sceneManager.stop(); // libera el rAF para que el captura headless pueda asentarse
      })
      .catch((err) => console.error('[Teyco] Error en preview de ficha:', err));
  }

  if (debugSave) {
    dimensions
      .loadFont(ASSETS.font)
      .then(async () => {
        let msg: string;
        try {
          const r = await saveCurrentQuote();
          msg = `[${r.via}] ${r.message}`;
        } catch (err) {
          msg = `ERROR: ${String(err)}`;
        }
        console.info('[Teyco][save-test]', msg);
        const div = document.createElement('div');
        div.textContent = msg;
        Object.assign(div.style, {
          position: 'fixed',
          top: '80px',
          left: '20px',
          right: '20px',
          padding: '24px',
          background: '#fff',
          color: '#000',
          font: '600 22px sans-serif',
          borderRadius: '10px',
          zIndex: '9999',
        });
        document.body.appendChild(div);
        sceneManager.stop();
      })
      .catch((err) => console.error('[Teyco] Error en test de guardado:', err));
  }

  // Conexión store → 3D en vivo.
  store.subscribe(({ key }) => {
    if (key === 'step') return;
    const cfg = resolveConfig(store.getState(), pergola.lamasOpen);
    if (key === 'color-estructura') {
      pergola.setColorHex(cfg.colorHex); // color no afecta geometría
    } else if (key === 'color-lamas') {
      pergola.setLamasColorHex(cfg.lamasColorHex);
    } else {
      // extras (LED), led-color, dimensiones, etc. → reconstruir.
      pergola.rebuild(cfg);
      dimensions.update(cfg);
      applyStage(cfg);
      sceneManager.frameOn(pergola.totalHeight);
    }
  });

  wireViewportButtons(sceneManager, pergola, dimensions);

  // Hooks de ambiente para QA/demo por URL.
  if (params.has('sun')) sceneManager.setSunPosition(Number(params.get('sun')));
  if (params.has('night')) sceneManager.setNight(true);
  if (params.has('panel')) (document.getElementById('btn-light') as HTMLButtonElement | null)?.click();

  // Exponer para depuración manual desde la consola.
  (window as unknown as Record<string, unknown>).teyco = { sceneManager, pergola, store };

  // Selector de idioma (placeholder de i18n; ES por defecto, EN preparado).
  const langSelect = document.getElementById('lang') as HTMLSelectElement | null;
  langSelect?.addEventListener('change', () => {
    console.info(`[Teyco] Idioma seleccionado: ${langSelect.value}`);
  });

  console.info('[Teyco] Configurador inicializado (entorno + 3D + cotas + CPQ + ficha + guardado).');
}

/**
 * Sobrescribe el estado inicial desde parámetros de URL.
 * Ej.: ?tipo=adosada&postes=6&color=RAL9016&anclaje=tarima&w=4000&l=5000&h=2800&lamas=open
 * @returns estado inicial del toggle de lamas (no vive en el wizard).
 */
function applyUrlOverrides(store: ConfigStore): boolean {
  const q = new URLSearchParams(window.location.search);

  const tipo = q.get('tipo');
  if (tipo === 'adosada' || tipo === 'independiente') store.set('tipo', tipo);

  const postes = q.get('postes');
  if (postes === '6') store.set('postes', '6-reforzados');
  else if (postes === '4') store.set('postes', '4-estandar');

  const anclaje = q.get('anclaje');
  if (anclaje && findOption('anclaje', anclaje)) store.set('anclaje', anclaje);

  const color = q.get('color');
  if (color && findOption('color-estructura', color)) store.set('color-estructura', color);

  const h = q.get('h');
  if (h && findOption('altura', h)) store.set('altura', h);

  const w = Number(q.get('w'));
  const l = Number(q.get('l'));
  if (w || l) {
    store.set('dimensiones-modo', 'personalizado');
    if (w) store.set('ancho-custom', w);
    if (l) store.set('largo-custom', l);
  }

  const colorLamas = q.get('color-lamas');
  if (colorLamas && findOption('color-lamas', colorLamas)) store.set('color-lamas', colorLamas);

  const rot = q.get('rot');
  if (rot === 'izquierda' || rot === 'derecha') store.set('lamas-rotacion', rot);

  // Toggles sí/no y color de luz.
  for (const key of ['led', 'sensor', 'motor']) {
    if (q.get(key) === '1' || q.get(key) === 'si') store.set(key, 'si');
  }
  const ledColor = q.get('led-color');
  if (ledColor && findOption('led-color', ledColor)) store.set('led-color', ledColor);

  for (const side of ['frontal', 'trasero', 'izquierda', 'derecha']) {
    const v = q.get(`screen-${side}`);
    if (v && findOption(`screen-${side}`, v)) store.set(`screen-${side}`, v);
  }

  const step = Number(q.get('step'));
  if (step >= 1) store.setStep(step - 1); // ?step=1..4 (1-based)

  const lamas = q.get('lamas');
  return lamas === 'open' || lamas === '1' || lamas === 'true';
}

/** Conecta los botones flotantes: iluminación, lamas y cotas. */
function wireViewportButtons(sceneManager: SceneManager, pergola: Pergola, dimensions: Dimensions): void {
  const btnDims = document.getElementById('btn-dims') as HTMLButtonElement | null;
  if (btnDims) {
    btnDims.setAttribute('aria-pressed', String(dimensions.visible));
    btnDims.addEventListener('click', () => {
      const on = dimensions.toggle();
      btnDims.setAttribute('aria-pressed', String(on));
    });
  }

  const btnLamas = document.getElementById('btn-lamas') as HTMLButtonElement | null;
  if (btnLamas) {
    btnLamas.setAttribute('aria-pressed', String(pergola.lamasOpen));
    btnLamas.addEventListener('click', () => {
      const open = pergola.toggleLamas();
      btnLamas.setAttribute('aria-pressed', String(open));
      const label = btnLamas.querySelector('.vp-label');
      if (label) label.textContent = open ? 'Abiertas' : 'Lamas';
    });
  }

  const btnLight = document.getElementById('btn-light') as HTMLButtonElement | null;
  const container = document.getElementById('viewport-pane');
  if (btnLight && container) {
    btnLight.setAttribute('aria-pressed', 'false');
    new SceneControls(sceneManager, btnLight, container);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
